import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';

function dateLabel(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

function healthMeta(status) {
  if (status === 'healthy') return { label: 'Healthy', classes: 'bg-green-100 text-green-700' };
  if (status === 'stale') return { label: 'Sync stale', classes: 'bg-amber-100 text-amber-800' };
  if (status === 'needs_attention') return { label: 'Action required', classes: 'bg-red-100 text-red-700' };
  if (status === 'waiting_for_first_sync') return { label: 'Waiting for first sync', classes: 'bg-blue-100 text-blue-700' };
  return { label: 'Not connected', classes: 'bg-gray-100 text-gray-600' };
}

function HealthCard({ title, subtitle, health, onSync, syncing }) {
  const meta = healthMeta(health?.status);
  const recovery = health?.recovery;
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div><h3 className="font-semibold text-gray-900">{title}</h3><p className="mt-0.5 text-xs text-gray-500">{subtitle}</p></div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${meta.classes}`}>{meta.label}</span>
      </div>
      <div className="mt-3 space-y-1 text-sm text-gray-500">
        <p>Last successful sync: <strong className="text-gray-700">{dateLabel(health?.lastSyncAt)}</strong></p>
        {health?.lastAttemptAt && <p>Last attempt: <strong className="text-gray-700">{dateLabel(health.lastAttemptAt)}</strong></p>}
      </div>
      {health?.error && <p className="mt-2 text-sm text-red-700">{health.error.message || health.error.code || String(health.error)}</p>}
      {recovery && (
        <div className={`mt-3 rounded-lg p-3 text-sm ${recovery.severity === 'error' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
          <p>{recovery.description}</p>
          {recovery.action?.type === 'link' && (
            <a href={recovery.action.href} className="mt-2 inline-flex font-semibold underline underline-offset-2">{recovery.action.label}</a>
          )}
          {recovery.action?.type === 'sync' && (
            <button onClick={onSync} disabled={syncing} className="mt-2 font-semibold underline underline-offset-2 disabled:opacity-50">
              {syncing ? 'Syncing…' : recovery.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function InboxOperationsPanel() {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/operations/inbox-health');
      setHealth(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reviewItems = useMemo(() => health?.reviewItems || [], [health]);

  const syncNow = async () => {
    setBusy('sync'); setNotice(''); setError('');
    try {
      const response = await api.post('/operations/inbox-sync/run');
      const linkedin = response.data?.linkedin?.processed || 0;
      const email = response.data?.email?.processed || 0;
      const failures = response.data?.email?.failures?.length || 0;
      setNotice(failures
        ? `Inbox sync completed with ${failures} mailbox issue${failures === 1 ? '' : 's'}. Recovery actions are shown below.`
        : `Inbox sync completed: ${linkedin} LinkedIn and ${email} email replies processed.`);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
      await load();
    } finally { setBusy(''); }
  };

  const resolve = async (itemId, campaignId) => {
    setBusy(itemId); setNotice(''); setError('');
    try {
      await api.post(`/operations/inbox-review/${itemId}/resolve`, { campaignId });
      setNotice('Reply assigned and processed. Agent context has been refreshed.');
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setBusy(''); }
  };

  const dismiss = async (itemId) => {
    setBusy(itemId); setNotice(''); setError('');
    try {
      await api.post(`/operations/inbox-review/${itemId}/dismiss`);
      setNotice('Inbox review item dismissed.');
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setBusy(''); }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Inbox sync & recovery</h2>
          <p className="text-sm text-gray-500">Live reply detection, connection health and messages that need human routing.</p>
        </div>
        <button onClick={syncNow} disabled={busy === 'sync'} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          {busy === 'sync' ? 'Syncing…' : 'Sync inboxes now'}
        </button>
      </div>

      {notice && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{notice}</div>}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <HealthCard title="LinkedIn inbox" subtitle={health?.linkedin?.sessionStatus || 'Session'} health={health?.linkedin} onSync={syncNow} syncing={busy === 'sync'} />
        {(health?.mailboxes || []).map((mailbox) => (
          <HealthCard key={`${mailbox.provider}:${mailbox.email}`} title={mailbox.provider === 'google' ? 'Gmail / Google Workspace' : 'Microsoft 365 / Outlook'} subtitle={mailbox.email} health={mailbox} onSync={syncNow} syncing={busy === 'sync'} />
        ))}
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="border-b px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <div><h3 className="font-semibold text-gray-900">Replies needing review</h3><p className="text-sm text-gray-500">Only shown when Leadgenflow cannot safely determine which campaign owns an inbound email.</p></div>
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">{reviewItems.length}</span>
          </div>
        </div>
        <div className="divide-y">
          {reviewItems.length === 0 && <div className="p-5 text-sm text-gray-500">No ambiguous replies waiting for review.</div>}
          {reviewItems.map((item) => (
            <div key={item._id} className="p-5 space-y-3">
              <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                <div><p className="font-medium text-gray-900">{item.subject || '(No subject)'}</p><p className="text-sm text-gray-500">From {item.sender} · {dateLabel(item.receivedAt)}</p></div>
                <button onClick={() => dismiss(item._id)} disabled={busy === item._id} className="text-sm font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50">Dismiss</button>
              </div>
              {item.message && <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 line-clamp-4">{item.message}</p>}
              {!item.message && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">This item predates message-body storage. Run a new sync; if the provider returns the message again, it will become replayable.</p>}
              <div className="grid gap-2 lg:grid-cols-2">
                {(item.candidateMatches || []).map((candidate) => (
                  <button key={`${candidate.campaignId}:${candidate.leadId}`} onClick={() => resolve(item._id, candidate.campaignId)} disabled={busy === item._id || !item.message} className="rounded-lg border p-3 text-left hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50">
                    <span className="block text-sm font-semibold text-gray-900">{candidate.campaignTitle || 'Campaign'}</span>
                    <span className="mt-1 block text-xs text-gray-500">{candidate.active ? 'Active campaign' : 'Inactive campaign'}{candidate.lastOutboundAt ? ` · last outreach ${dateLabel(candidate.lastOutboundAt)}` : ''}</span>
                    <span className="mt-2 block text-xs font-semibold text-blue-600">Assign reply here</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
